/**
 * VehicleMonitoringAdvanceSearch Component
 *
 * Advanced search / filter form for the Monitoring MAP page.
 */

import React, {useEffect, useState} from 'react'
import axios from 'axios'
import Select from 'react-select'
import {Form, FormikProvider, useFormik} from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import {useDataLayerValue} from '../../../DataLayer'

const API_URL = process.env.REACT_APP_API_URL

export type ActiveFilterChip = {
  id: string
  label: string
  value: string
}

type JobMonitoringFilterProps = {
  setFilterOptions: (options: any) => void
  onActiveFiltersChange?: (chips: ActiveFilterChip[]) => void
  /** Increment to force form reset + apply defaults (e.g. clear-all chips) */
  resetToken?: number
}

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    lineHeight: '24px',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 999,
  }),
}

const advanceSerachSchema = Yup.object().shape({
  jobStatusCodes: Yup.array(),
  requestCustomer: Yup.array(),
  shipper: Yup.array(),
  vehicleType: Yup.array(),
  statusCode: Yup.string().nullable(),
  fromDate: Yup.date().nullable(),
  toDate: Yup.date().nullable(),
  requestId: Yup.string().nullable(),
  docConfirmation: Yup.array().nullable(),
  jobCode: Yup.string().nullable(),
  vehicleRegNumber: Yup.string().nullable(),
  flagStatus: Yup.string().nullable(),
  cT_FK_OperatingTypeCode: Yup.string().nullable(),
  cT_FK_CustomerSpecificationCode: Yup.string().nullable(),
})

export interface FilterOptions {
  requestId: string | null
  fromDate: string | null
  toDate: string | null
  docConfirmation: []
  jobStatusCodes: []
  requestCustomer: []
  shipper: []
  jobCode: string | null
  vehicleRegNumber: string | null
  vehicleType: []
  flagStatus: string | null
  statusCode: string | null
  cT_FK_OperatingTypeCode: string | null
  cT_FK_CustomerSpecificationCode: string | null
}

export const filterInitials: FilterOptions = {
  requestId: null,
  fromDate: null,
  toDate: null,
  docConfirmation: [],
  jobStatusCodes: [],
  requestCustomer: [],
  shipper: [],
  jobCode: null,
  vehicleRegNumber: null,
  vehicleType: [],
  statusCode: null,
  flagStatus: null,
  cT_FK_OperatingTypeCode: null,
  cT_FK_CustomerSpecificationCode: null,
}

function buildActiveFilterChips(
  values: FilterOptions,
  lookups: {
    requestCustomers: any[]
    statusCodes: any[]
    operatingTypes: any[]
    customerSpecifications: any[]
  }
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []

  const customerLabels = (values.requestCustomer as unknown as string[])
    ?.map(
      (key) => lookups.requestCustomers?.find((item: any) => item.key === key)?.value ?? String(key)
    )
    .filter(Boolean)
  if (customerLabels?.length) {
    chips.push({id: 'requestCustomer', label: 'Customer', value: customerLabels.join(', ')})
  }

  if (values.fromDate) {
    chips.push({id: 'fromDate', label: 'From', value: String(values.fromDate)})
  }
  if (values.toDate) {
    chips.push({id: 'toDate', label: 'To', value: String(values.toDate)})
  }

  const statusLabels = (values.jobStatusCodes as unknown as string[])
    ?.map(
      (key) => lookups.statusCodes?.find((item: any) => item.key === key)?.value ?? String(key)
    )
    .filter(Boolean)
  if (statusLabels?.length) {
    chips.push({id: 'jobStatusCodes', label: 'Shipment Status', value: statusLabels.join(', ')})
  }

  if (values.cT_FK_OperatingTypeCode) {
    const label =
      lookups.operatingTypes?.find(
        (item: any) => item.key === values.cT_FK_OperatingTypeCode
      )?.value ?? String(values.cT_FK_OperatingTypeCode)
    chips.push({id: 'cT_FK_OperatingTypeCode', label: 'Operating Type', value: label})
  }

  if (values.cT_FK_CustomerSpecificationCode) {
    const label =
      lookups.customerSpecifications?.find(
        (item: any) => item.key === values.cT_FK_CustomerSpecificationCode
      )?.value ?? String(values.cT_FK_CustomerSpecificationCode)
    chips.push({
      id: 'cT_FK_CustomerSpecificationCode',
      label: 'Customer Spec',
      value: label,
    })
  }

  return chips
}

function toAppliedFilterOptions(values: FilterOptions) {
  return {
    statusCode: values.statusCode != '' ? values.statusCode : null,
    fromDate: values.fromDate != '' ? values.fromDate : null,
    toDate: values.toDate != '' ? values.toDate : null,
    jobStatusCodes: values.jobStatusCodes,
    requestCustomer: values.requestCustomer,
    shipper: values.shipper,
    vehicleType: values.vehicleType,
    flagStatus:
      values.flagStatus === 'true' ? true : values.flagStatus === 'false' ? false : null,
    docConfirmation: values.docConfirmation,
    vehicleRegNumber: values.vehicleRegNumber,
    jobCode: values.jobCode,
    requestId: values.requestId,
    cT_FK_OperatingTypeCode:
      values.cT_FK_OperatingTypeCode != '' ? values.cT_FK_OperatingTypeCode : null,
    cT_FK_CustomerSpecificationCode:
      values.cT_FK_CustomerSpecificationCode != ''
        ? values.cT_FK_CustomerSpecificationCode
        : null,
  }
}

export function VehicleMonitoringAdvanceSearch({
  setFilterOptions,
  onActiveFiltersChange,
  resetToken = 0,
}: JobMonitoringFilterProps) {
  const [{company}] = useDataLayerValue()
  const [statusCodes, setStatusCodes] = useState<any[]>([])
  const [requestCustomers, setRequestCustomers] = useState<any>([])
  const [operatingTypes, setOperatingTypes] = useState<any[]>([])
  const [customerSpecifications, setCustomerSpecifications] = useState<any[]>([])

  useEffect(() => {
    fetchRequestCustomers()
    fetchJobOperationStatus()
    fetchOperatingTypes()
    fetchCustomerSpecifications()
  }, [])

  const fetchRequestCustomers = async () => {
    try {
      let response = await axios.get(`${API_URL}/customers/ddlist-customer-jobrequest/${company}`)
      setRequestCustomers(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchJobOperationStatus = async () => {
    try {
      let response = await axios.get(`${API_URL}/masterdata/ddlist-commontable/c38/${company}`)
      setStatusCodes(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchOperatingTypes = async () => {
    try {
      let response = await axios.get(`${API_URL}/masterdata/ddlist-commontable/C90/${company}`)
      setOperatingTypes(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchCustomerSpecifications = async () => {
    try {
      let response = await axios.get(`${API_URL}/masterdata/ddlist-commontable/C45/${company}`)
      setCustomerSpecifications(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const formik = useFormik({
    initialValues: filterInitials,
    validationSchema: advanceSerachSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        setFilterOptions(toAppliedFilterOptions(values))
        onActiveFiltersChange?.(
          buildActiveFilterChips(values, {
            requestCustomers,
            statusCodes,
            operatingTypes,
            customerSpecifications,
          })
        )
      } catch (error) {
        console.error(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (resetToken === 0) return
    formik.resetForm({values: filterInitials})
    setFilterOptions(toAppliedFilterOptions(filterInitials))
    onActiveFiltersChange?.([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken])

  return (
    <FormikProvider value={formik}>
      <Form>
        <div
          id='kt_accordion_1_body_1'
          className='accordion-collapse collapse p-3 border-0'
          aria-labelledby='kt_accordion_1_header_1'
          data-bs-parent='#kt_accordion_1'
        >
          <div className='row'>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Customer:
                </label>
                <div>
                  <Select
                    options={requestCustomers?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className={`react-select-styled form-select-sm form-select-solid w-100 m-0 p-0 ${
                      formik.errors?.requestCustomer && 'is-invalid'
                    }`}
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Customer'
                    isMulti
                    value={formik.values.requestCustomer?.map((statusKey: string) => ({
                      value: statusKey,
                      label: requestCustomers?.find((item: any) => item.key === statusKey)?.value,
                    }))}
                    onChange={(selectedOptions: any) => {
                      const values = selectedOptions
                        ? selectedOptions.map((option: any) => option.value)
                        : []
                      formik.setFieldValue('requestCustomer', values)
                    }}
                    onBlur={() => formik.setFieldTouched('requestCustomer', true)}
                  />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label
                  htmlFor='fromDate'
                  className={`form-label ${formik.values.statusCode ? 'required' : ''}`}
                  style={{fontSize: '12px'}}
                >
                  From Date:
                </label>
                <input
                  type='date'
                  className='form-control form-control-white form-select-sm '
                  placeholder='Enter From Date'
                  name='fromDate'
                  id='fromDate'
                  onChange={(e) => {
                    if (!e.target.value) {
                      formik.setFieldValue('toDate', null)
                    }
                    formik.handleChange(e)
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.fromDate ?? ''}
                  max={moment().format('YYYY-MM-DD')}
                />
                {formik.errors.fromDate && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{String(formik.errors?.fromDate ?? '')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label htmlFor='toDate' className='form-label' style={{fontSize: '12px'}}>
                  To Date:
                </label>
                <input
                  type='date'
                  className='form-control form-control-white form-select-sm '
                  placeholder='Enter To Date'
                  name='toDate'
                  id='toDate'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.toDate ?? ''}
                  min={moment(formik.values.fromDate).format('YYYY-MM-DD')}
                  disabled={formik.values.fromDate === null || formik.values.fromDate === ''}
                />
              </div>
            </div>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Shipment Status:
                </label>
                <div>
                  <Select
                    options={statusCodes
                      ?.filter(
                        (item: any) =>
                          item?.identification !== 'OC' && item?.identification !== 'CL'
                      )
                      ?.map((item: any) => ({
                        value: item.key,
                        label: item.value,
                      }))}
                    noOptionsMessage={() => 'No Options'}
                    className={`react-select-styled form-select-sm form-select-solid w-100 m-0 p-0 ${
                      formik.errors?.jobStatusCodes && 'is-invalid'
                    }`}
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Shipment Status'
                    isMulti
                    value={formik.values.jobStatusCodes?.map((statusKey: string) => ({
                      value: statusKey,
                      label: statusCodes?.find((item: any) => item.key === statusKey)?.value,
                    }))}
                    onChange={(selectedOptions: any) => {
                      const values = selectedOptions
                        ? selectedOptions.map((option: any) => option.value)
                        : []
                      formik.setFieldValue('jobStatusCodes', values)
                    }}
                    onBlur={() => formik.setFieldTouched('jobStatusCodes', true)}
                  />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Operating Type:
                </label>
                <div>
                  <Select
                    options={operatingTypes?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className='react-select-styled form-select-sm form-select-solid w-100 m-0 p-0'
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Operating Type'
                    isClearable
                    value={
                      formik.values.cT_FK_OperatingTypeCode
                        ? {
                            value: formik.values.cT_FK_OperatingTypeCode,
                            label: operatingTypes?.find(
                              (item: any) => item.key === formik.values.cT_FK_OperatingTypeCode
                            )?.value,
                          }
                        : null
                    }
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue(
                        'cT_FK_OperatingTypeCode',
                        selectedOption?.value ?? ''
                      )
                    }}
                    onBlur={() => formik.setFieldTouched('cT_FK_OperatingTypeCode', true)}
                  />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Customer Specification:
                </label>
                <div>
                  <Select
                    options={customerSpecifications?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className='react-select-styled form-select-sm form-select-solid w-100 m-0 p-0'
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Customer Specification'
                    isClearable
                    value={
                      formik.values.cT_FK_CustomerSpecificationCode
                        ? {
                            value: formik.values.cT_FK_CustomerSpecificationCode,
                            label: customerSpecifications?.find(
                              (item: any) =>
                                item.key === formik.values.cT_FK_CustomerSpecificationCode
                            )?.value,
                          }
                        : null
                    }
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue(
                        'cT_FK_CustomerSpecificationCode',
                        selectedOption?.value ?? ''
                      )
                    }}
                    onBlur={() =>
                      formik.setFieldTouched('cT_FK_CustomerSpecificationCode', true)
                    }
                  />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12 d-flex  justify-content-end align-items-end mb-2 w-100'>
              <div className='d-flex'>
                <button
                  type='reset'
                  onClick={(e) => {
                    formik.handleReset(e)
                    formik.setValues(filterInitials)
                    setFilterOptions(toAppliedFilterOptions(filterInitials))
                    onActiveFiltersChange?.([])
                  }}
                  className='btn btn-sm btn-light btn-active-light-primary me-2'
                >
                  Reset
                </button>

                <button
                  type='button'
                  onClick={() => {
                    formik.handleSubmit()
                  }}
                  disabled={formik.isSubmitting || !formik.isValid}
                  className='btn btn-sm btn-primary'
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </FormikProvider>
  )
}
